import { useMemo, useState } from "react";
import { Copy, Check, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { trackMetaEvent } from "@/lib/metaPixel";

interface Props {
  referralCode: string | null | undefined;
  foundingMember?: boolean | null;
}

/**
 * Simple parent-facing referral share card.
 * Links use `?ref=CODE` and are captured in Login.tsx → applied to the
 * household at creation in useHousehold.createHousehold.
 */
const ReferralShareCard = ({ referralCode, foundingMember }: Props) => {
  const [copied, setCopied] = useState(false);

  const shareUrl = useMemo(() => {
    if (!referralCode || typeof window === "undefined") return "";
    const origin = window.location.origin;
    return `${origin}/?ref=${referralCode}`;
  }, [referralCode]);

  // Production-safe display. We never show a raw Lovable preview URL
  // (sandbox.lovable.dev / *.lovableproject.com) directly to parents —
  // we surface a neutral "your invite link" pill and the bare ref code.
  const isPreviewHost = useMemo(() => {
    if (typeof window === "undefined") return false;
    const h = window.location.hostname;
    return /(lovable\.dev|lovableproject\.com|sandbox)/i.test(h);
  }, []);

  if (!referralCode) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 1800);
      void trackMetaEvent("referral_link_shared", {
        customData: { ref_code: referralCode, founding_member: !!foundingMember, method: "copy" },
      });
    } catch {
      toast.error("Couldn't copy — please copy the link manually.");
    }
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.07] bg-gradient-to-br from-card/85 via-card/65 to-card/45 p-4 backdrop-blur-xl shadow-[0_16px_40px_-30px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.05)] space-y-3">
      <div className="pointer-events-none absolute -top-12 -right-12 h-28 w-28 rounded-full bg-primary/10 blur-3xl" />
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-primary/10 ring-1 ring-primary/25 flex items-center justify-center shrink-0">
          <Gift className="w-4 h-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">
            Invite a family to HeirWise
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            When a friend's family joins and stays past their first paid month, you both get a free month on us.
          </p>
          {foundingMember && (
            <p className="text-[11px] font-medium text-primary mt-1">
              Founding family perk · thank you for being early.
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0 inline-flex items-center gap-2 rounded-xl border border-white/[0.07] bg-background/40 px-3 py-2 text-[11px] backdrop-blur">
          <span className="text-muted-foreground">Your invite code</span>
          <code className="font-mono text-foreground font-semibold tracking-wider">{referralCode}</code>
          {isPreviewHost && (
            <span className="ml-auto inline-flex items-center rounded-full border border-white/10 bg-background/50 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">
              Preview
            </span>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={copy} className="shrink-0 h-9 rounded-xl border-white/10 bg-background/40 backdrop-blur">
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          <span className="ml-1.5 text-xs">{copied ? "Copied" : "Copy link"}</span>
        </Button>
      </div>
      <p className="text-[10.5px] text-muted-foreground">
        We'll copy the full invite link to your clipboard. The link is safe to share over text or email.
      </p>
    </div>
  );
};

export default ReferralShareCard;